import { z } from 'zod';

/**
 * My Brand impression-device tokens (`core.raw_table_view`.`audience:impression_device`).
 *
 * Live BigQuery values on Google rows (video, demand gen, pmax, display) are
 * lowercase. Empty or every token means All. Product copy lives in the frontend
 * i18n files — this module owns wire values only.
 */

export const IMPRESSION_DEVICE_MOBILE = 'mobile';
export const IMPRESSION_DEVICE_CONNECTED_TV = 'connected_tv';
export const IMPRESSION_DEVICE_DESKTOP = 'desktop';
export const IMPRESSION_DEVICE_TABLET = 'tablet';

export const IMPRESSION_DEVICE_VALUES = [
  IMPRESSION_DEVICE_MOBILE,
  IMPRESSION_DEVICE_CONNECTED_TV,
  IMPRESSION_DEVICE_DESKTOP,
  IMPRESSION_DEVICE_TABLET,
] as const;

export type ImpressionDeviceValue = (typeof IMPRESSION_DEVICE_VALUES)[number];

export const ImpressionDeviceValueSchema = z.enum(IMPRESSION_DEVICE_VALUES);

/**
 * `core.raw_table_view.source` values that carry impression-device data.
 * My Brand splits Google into these campaign types. `demand gen` and
 * `demand_gen` both title-case to "Demand Gen".
 */
export const GOOGLE_IMPRESSION_DEVICE_SOURCES = [
  'demand gen',
  'demand_gen',
  'pmax',
  'video',
  'display',
] as const;

const GOOGLE_IMPRESSION_DEVICE_SOURCE_SET = new Set<string>(GOOGLE_IMPRESSION_DEVICE_SOURCES);

export function isGoogleImpressionDeviceSource(platform: string): boolean {
  return GOOGLE_IMPRESSION_DEVICE_SOURCE_SET.has(platform.trim().toLowerCase());
}

const IMPRESSION_DEVICE_VALUE_SET = new Set<string>(IMPRESSION_DEVICE_VALUES);

function uniquePreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    if (seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

function isAllImpressionDevices(values: string[]): boolean {
  if (values.length !== IMPRESSION_DEVICE_VALUES.length) return false;
  return values.every((value) => IMPRESSION_DEVICE_VALUE_SET.has(value))
    && new Set(values).size === IMPRESSION_DEVICE_VALUES.length;
}

/**
 * Canonical BQ impression-device list.
 * Empty / all four UI tokens => All (`[]`).
 */
export function toBqImpressionDevice(values: string[]): string[] {
  const unique = uniquePreserveOrder(values.map((value) => value.trim()).filter((value) => value !== ''));
  if (unique.length === 0 || isAllImpressionDevices(unique)) return [];
  return unique;
}
